import React from 'react';
import { Grid2, MenuItem, Select, TextField } from '@mui/material';
import AutocompleteSelect from '../../../components/atom/AutoCompleteSelect';
import BlockWithTitle from '../../../components/atom/BlockWithTitle';
import CommonButton from '../../../components/atom/CommonButton';
import UserCapabilitiesSection from './UserCapabilitiesSection';
import { venuesAutocomplete } from '../../../autocomplete';
import { ROLE_DEFAULTS, getAssignableTemplates } from '../../../helpers/capabilities';

const DEFAULT_NON_SUPERADMIN_ROLE = 'agent';

const EditUserProfileTab = ({
    t,
    values,
    setFieldValue,
    touched,
    errors,
    roles,
    venueId,
    navigate,
    isSubmitting,
    editUserMutation,
    addUserMutation,
    isSuperAdmin,
    currentUserCapabilities,
    canUsersManage,
    isEditingSelf,
}) => {
    const displayRole = values.role === 'superadmin' ? 'superadmin' : 'user';

    const handleRoleChange = (newDisplayRole) => {
        if (newDisplayRole === 'superadmin') {
            setFieldValue('role', 'superadmin');
            setFieldValue('capabilities', []);
        } else {
            const backendRole = DEFAULT_NON_SUPERADMIN_ROLE;
            setFieldValue('role', backendRole);
            const defaults = ROLE_DEFAULTS[backendRole];
            if (defaults) {
                setFieldValue('capabilities', [...defaults]);
            }
        }
    };

    const showCapabilities = canUsersManage && values.role !== 'superadmin' && values.capabilities;

    // Filter presets using the same management-envelope policy as the backend
    const allowedTemplates = isSuperAdmin
        ? undefined // all templates
        : getAssignableTemplates(currentUserCapabilities, false);

    return (
        <BlockWithTitle title={t('users.userDetails')}>
            <Grid2 size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    label={t('users.columns.name')}
                    value={values.name}
                    onChange={(ev) => setFieldValue('name', ev.target.value)}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name ? errors.name : ''}
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    label={t('users.columns.email')}
                    value={values.email}
                    onChange={(ev) => setFieldValue('email', ev.target.value)}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email ? errors.email : ''}
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <AutocompleteSelect
                    initCall={venuesAutocomplete.init}
                    autocompleteCall={venuesAutocomplete.search}
                    value={values.venueId}
                    setValue={(ev) => setFieldValue('venueId', ev)}
                    disabled
                >
                    <TextField label={t('users.venueSelect')} fullWidth required />
                </AutocompleteSelect>
            </Grid2>
            {isSuperAdmin && (
                <Grid2 size={{ xs: 12 }}>
                    <Select
                        sx={{ textAlign: 'left' }}
                        fullWidth
                        label={t('users.columns.role')}
                        value={displayRole}
                        onChange={(ev) => handleRoleChange(ev.target.value)}
                    >
                        {roles.map((role) => (
                            <MenuItem value={role} key={role}>
                                {t(`users.roleLabels.${role}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid2>
            )}
            {showCapabilities && (
                <Grid2 size={{ xs: 12 }}>
                    <UserCapabilitiesSection
                        capabilities={values.capabilities}
                        setCapabilities={(caps) => setFieldValue('capabilities', caps)}
                        role={values.role}
                        setRole={(r) => setFieldValue('role', r)}
                        allowedTemplates={allowedTemplates}
                        isEditingSelf={isEditingSelf}
                        currentUserIsSuperAdmin={isSuperAdmin}
                        currentUserCapabilities={currentUserCapabilities}
                    />
                </Grid2>
            )}
            <Grid2 size={{ xs: 6, md: 2 }}>
                <CommonButton
                    fullWidth
                    onClick={() => navigate(`/venues/${venueId}/users`)}
                    label={t('common.cancel')}
                    buttonType="secondary"
                />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 2 }}>
                <CommonButton
                    fullWidth
                    disabled={
                        isSubmitting || editUserMutation.isPending || addUserMutation.isPending
                    }
                    type="submit"
                    label={t('common.save')}
                    buttonType="primary"
                />
            </Grid2>
        </BlockWithTitle>
    );
};

export default EditUserProfileTab;
